import { createAction, handleActions } from 'redux-actions';
import { client as apollo } from 'cccisd-apollo';

// Queries
import providersQuery from '../queries/providers.graphql';
import supervisorProvidersQuery from '../queries/supervisorProviders.graphql';
import groupingUnitsQuery from '../queries/groupingUnits.graphql';
import learnersQuery from '../queries/learners.graphql';
import assessmentPlansQuery from '../queries/createdAssessmentPlans.graphql';

const Fortress = window.cccisd.fortress;

export const initialState = {
    selectedGU: null, // i.e. District
    selectedProvider: null,
    providers: [],
    groupingUnits: [],
    students: [],
    selectedStudent: null,
    assessmentPlans: [],
    pawnInfo: {},
    progress: {},
};

// Actions
const SET_SELECTED_PROVIDER = 'app/share/SET_SELECTED_PROVIDER';
const SET_SELECTED_GU = 'app/share/SET_SELECTED_GU';
const SET_GROUPING_UNITS = 'app/share/SET_GROUPING_UNITS';
const SET_PROVIDERS = 'app/share/SET_PROVIDERS';
const SET_STUDENTS = 'app/share/SET_STUDENTS';
const SET_SELECTED_STUDENT = 'app/share/SET_SELECTED_STUDENT';
const SET_ASSESSMENT_PLANS = 'app/share/SET_ASSESSMENT_PLANS';
const APPEND_ASSESSMENT_PLANS = 'app/share/APPEND_ASSESSMENT_PLANS';
const SET_PAWN_INFO = 'app/share/SET_PAWN_INFO';
const SET_PROGRESS = 'app/share/SET_PROGRESS';

// Action Creators
export const setSelectedProvider = createAction(SET_SELECTED_PROVIDER);
export const setSelectedGU = createAction(SET_SELECTED_GU);
export const setProviders = createAction(SET_PROVIDERS);
export const setGroupingUnits = createAction(SET_GROUPING_UNITS);
export const setStudents = createAction(SET_STUDENTS);
export const setSelectedStudent = createAction(SET_SELECTED_STUDENT);
export const setAssessmentPlans = createAction(SET_ASSESSMENT_PLANS);
export const appendAssessmentPlans = createAction(APPEND_ASSESSMENT_PLANS);
export const setPawnInfo = createAction(SET_PAWN_INFO);
export const setProgress = createAction(SET_PROGRESS);

export const loadAssessmentsAndContacts = ({
    limit = null,
    offset = 0,
    append = false,
    orderBy = [{ field: 'createdAt', direction: 'DESC' }],
    filter = null,
} = {}) => {
    return async (dispatch, getState) => {
        let result = null;
        try {
            const variables = {
                pawnId: Fortress.user.acting.id,
                orderBy,
            };

            if (filter !== null) {
                variables.filter = filter;
            }

            if (limit !== null) {
                variables.limit = limit;
                variables.offset = offset;
            }

            result = await apollo.query({
                query: assessmentPlansQuery,
                variables,
                fetchPolicy: 'network-only',
            });
            let planList = result.data.roles.instructor.createdAssignmentPlanList.filter(
                plan => plan.selectedByPawn.pawn.pawnId !== null
            );

            if (append) {
                dispatch(appendAssessmentPlans(planList));
            } else {
                dispatch(setAssessmentPlans(planList));
            }

            const existingPawnInfo = append ? getState().app.share.pawnInfo : {};
            const pawnInfo = { ...existingPawnInfo };

            planList.forEach(plan => {
                let studentSchool = plan.selectedByPawn.parentGroup.site.label;
                pawnInfo[plan.selectedByPawn.pawn.pawnId] = {
                    pawnId: plan.selectedByPawn.pawn.pawnId,
                    pawnHash: plan.selectedByPawn.pawn.pawnHash,
                    name: `${plan.selectedByPawn.fields.firstName} ${plan.selectedByPawn.fields.lastName}`,
                    email: plan.selectedByPawn.fields.email,
                    studentId: plan.selectedByPawn.fields.studentId,
                    group: studentSchool,
                    reporterType: 'Student',
                    messageList: plan.selectedByPawn.messageList,
                };
                plan.selectedByPawn.childRoles.contactList.forEach(contact => {
                    pawnInfo[contact.pawn.pawnId] = {
                        pawnId: contact.pawn.pawnId,
                        pawnHash: contact.pawn.pawnHash,
                        name: contact.fields.contactName,
                        email: contact.fields.contactEmail,
                        group: studentSchool,
                        reporterType: contact.fields.contactType,
                        messageList: contact.messageList,
                    };
                });
            });
            dispatch(setPawnInfo(pawnInfo));
            return { planList, hasMore: limit ? planList.length === limit : false };
        } catch (e) {
            throw e;
        }
    };
};

export const loadProgress = ({ plan, progressQuery }) => {
    return async (dispatch, getState) => {
        let data = [];
        const combinedSessionsData = {
            deployments: new Set(),
            surveyList: new Set(),
            pawnIds: new Set(),
        };

        plan.sessionList.forEach(session => {
            if (session.sessionDeploymentInfo) {
                Object.values(session.sessionDeploymentInfo).forEach(s => {
                    combinedSessionsData.deployments.add(s.deploymentId);
                    combinedSessionsData.pawnIds.add(s.pawnId);
                });
            }
            session.settings.options.forEach(surveyHandle => combinedSessionsData.surveyList.add(surveyHandle));
        });

        try {
            const resp = await apollo.query({
                query: progressQuery,
                variables: {
                    sessionIds: plan.sessionList.map(session => session.sessionId),
                    pawnIds: [...combinedSessionsData.pawnIds],
                },
                fetchPolicy: 'network-only',
            });

            data = resp.data.flows.assignmentProgressList;
            dispatch(setProgress({ ...getState().app.share.progress, [plan.assignmentPlanId]: data }));
        } catch (e) {
            throw e;
        }
    };
};

export const loadGroupingUnits = () => {
    return async (dispatch, getState) => {
        let result = null;
        try {
            result = await apollo.query({
                query: groupingUnitsQuery,
                variables: {},
                fetchPolicy: 'network-only',
            });
            // console.log(data);
            dispatch(setGroupingUnits(result.data.groups.groupingUnitList));
            // dispatch(setSelectedGU(result.data.groups.groupingUnitList[0].group.groupId));
            return result;
        } catch (e) {
            throw e;
        }
    };
};

export const loadProviders = ({ guId, role }) => {
    return async (dispatch, getState) => {
        let result = null;
        try {
            result = await apollo.query({
                query: role === 'supervisor' ? supervisorProvidersQuery : providersQuery,
                variables:
                    role === 'supervisor'
                        ? {}
                        : {
                              guId,
                          },
                fetchPolicy: 'network-only',
            });
            dispatch(setProviders(result.data.roles.instructorList));
            return result;
        } catch (e) {
            throw e;
        }
    };
};

export const loadStudents = () => {
    return async (dispatch, getState) => {
        let result = null;
        try {
            result = await apollo.query({
                query: learnersQuery,
                variables: {},
                fetchPolicy: 'network-only',
            });
            dispatch(setStudents(result.data.roles.learnerList));
            return result;
        } catch (e) {
            throw e;
        }
    };
};

// reducer
export default handleActions(
    {
        [SET_SELECTED_PROVIDER]: (state, action) => {
            return {
                ...state,
                selectedProvider: action.payload,
            };
        },
        [SET_PROVIDERS]: (state, action) => {
            return {
                ...state,
                providers: action.payload,
            };
        },
        [SET_GROUPING_UNITS]: (state, action) => {
            return {
                ...state,
                groupingUnits: action.payload,
            };
        },
        [SET_SELECTED_GU]: (state, action) => {
            return {
                ...state,
                selectedGU: action.payload,
            };
        },
        [SET_STUDENTS]: (state, action) => {
            return {
                ...state,
                students: action.payload,
            };
        },
        [SET_SELECTED_STUDENT]: (state, action) => {
            return {
                ...state,
                selectedStudent: action.payload,
            };
        },
        [SET_ASSESSMENT_PLANS]: (state, action) => {
            return {
                ...state,
                assessmentPlans: action.payload,
            };
        },
        [APPEND_ASSESSMENT_PLANS]: (state, action) => {
            return {
                ...state,
                assessmentPlans: [...state.assessmentPlans, ...action.payload],
            };
        },
        [SET_PAWN_INFO]: (state, action) => {
            return {
                ...state,
                pawnInfo: action.payload,
            };
        },
        [SET_PROGRESS]: (state, action) => {
            return {
                ...state,
                progress: action.payload,
            };
        },
    },
    initialState
);
